import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Plus } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import type { TribeMembership } from "@/types/tribe";
import type { SpaceMemberRole } from "@/lib/supabase";

// Deterministic pastel palette for space initial circles when no avatar.
const SPACE_COLORS = [
  "#E4C8B8", "#C8D8C8", "#C8D0E4", "#E4D0C8",
  "#D4C8E4", "#E4D8C8", "#C8E4DC", "#DCCBE4",
];
function colorForSpaceId(spaceId: string): string {
  let hash = 0;
  for (let i = 0; i < spaceId.length; i++) hash = (hash << 5) - hash + spaceId.charCodeAt(i);
  return SPACE_COLORS[Math.abs(hash) % SPACE_COLORS.length];
}

interface SpaceSwitcherProps {
  spaces: TribeMembership[];
  activeSpaceId: string | null;
  onSwitch: (spaceId: string) => void;
  onCreate: () => void;
}

function roleBadgeKey(role: SpaceMemberRole): string | null {
  if (role === "owner") return "spaces.switcher.ownerBadge";
  if (role === "member") return "spaces.switcher.memberBadge";
  if (role === "contributor") return "spaces.switcher.contributorBadge";
  return null;
}

const SpaceAvatar = ({
  space,
  size = 20,
}: {
  space: TribeMembership;
  size?: number;
}) => {
  // MVP: first-letter circle. Signed-URL resolution for spaces.avatar_url
  // lands in a follow-up (mirrors ChildAvatar's caching pattern).
  const letter = (space.spaceName?.charAt(0) ?? "?").toUpperCase();
  return (
    <span
      className="inline-flex items-center justify-center rounded-full font-semibold text-foreground shrink-0"
      style={{
        width: size,
        height: size,
        backgroundColor: colorForSpaceId(space.spaceId),
        fontSize: Math.max(10, Math.round(size * 0.5)),
      }}
      aria-hidden
    >
      {letter}
    </span>
  );
};

const SpaceSwitcher = ({
  spaces,
  activeSpaceId,
  onSwitch,
  onCreate,
}: SpaceSwitcherProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  // Hide the chip when the user is only in one space. The "+ Create my own
  // space" path for single-membership contributors lives on the Profile
  // page (Chunk 4) as a separate entry point.
  if (spaces.length < 2) return null;

  const active = spaces.find((s) => s.spaceId === activeSpaceId) ?? spaces[0];

  const handleSpaceTap = (spaceId: string) => {
    setOpen(false);
    if (spaceId !== active.spaceId) onSwitch(spaceId);
  };

  const handleCreateTap = () => {
    setOpen(false);
    onCreate();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-1.5 rounded-full bg-card/90 px-2.5 py-1 text-xs font-semibold text-foreground shadow-sm transition-transform active:scale-95 hover:scale-[1.03]"
          style={{ maxWidth: 180 }}
        >
          <SpaceAvatar space={active} size={18} />
          <span className="truncate">{active.spaceName}</span>
          <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-0">
        <div className="px-3 pt-3 pb-1">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
            {t("spaces.switcher.title")}
          </p>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {spaces.map((space) => {
            const isActive = space.spaceId === active.spaceId;
            const badgeKey = roleBadgeKey(space.role);
            return (
              <button
                key={space.spaceId}
                type="button"
                onClick={() => handleSpaceTap(space.spaceId)}
                className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left hover:bg-muted/60 transition-colors"
              >
                <SpaceAvatar space={space} size={24} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    {space.spaceName}
                  </p>
                  {badgeKey && (
                    <p className="text-[10px] text-muted-foreground">
                      {t(badgeKey)}
                    </p>
                  )}
                </div>
                <span
                  className={`h-2 w-2 rounded-full shrink-0 ${
                    isActive ? "bg-primary" : "bg-transparent border border-border"
                  }`}
                  aria-hidden
                />
              </button>
            );
          })}
        </div>
        <div className="border-t border-border/50">
          <button
            type="button"
            onClick={handleCreateTap}
            className="flex w-full items-center gap-2.5 px-3 py-3 text-left hover:bg-muted/60 transition-colors"
          >
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary shrink-0">
              <Plus className="h-3.5 w-3.5" />
            </span>
            <span className="text-sm font-medium text-foreground">
              {t("spaces.switcher.createMyOwn")}
            </span>
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default SpaceSwitcher;
